import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, UserCheck, Search, Plus, X, Send, Trophy, ExternalLink, AlertTriangle, RefreshCw } from 'lucide-react';
import { VUKZ, VUKZ_GRADIENT } from '../theme/vukzTheme';
import PageHeader from '../components/layout/PageHeader';
import { AZURE } from '../services/api';
import { useActiveContext } from '../contexts/ActiveContextContext';
import { getOfficialLinkStatus, getOfficialAthletes } from '../services/officialDataService';
import { useSportsLang } from '../contexts/SportsLangContext';

const ROLE_LABELS = {
  pt: { TREINADOR: 'Treinador', PREPARADOR_FISICO: 'Preparador Físico', NUTRICIONISTA: 'Nutricionista', FISIOTERAPEUTA: 'Fisioterapeuta', MEDICO: 'Médico', ANALISTA: 'Analista', OUTRO: 'Outro' },
  en: { TREINADOR: 'Coach', PREPARADOR_FISICO: 'Physical Trainer', NUTRICIONISTA: 'Nutritionist', FISIOTERAPEUTA: 'Physiotherapist', MEDICO: 'Physician', ANALISTA: 'Analyst', OUTRO: 'Other' },
};

const LABELS = {
  pt: {
    addStaffTitle: 'Cadastrar Staff',
    labelFullName: 'Nome completo *',
    labelEmail: 'E-mail',
    labelRole: 'Função *',
    saving: 'Salvando...',
    btnAddStaff: 'Cadastrar Staff',
    pageTitle: 'Staff',
    pageSubtitle: 'Equipe técnica do clube · atletas nas categorias',
    pageSubtitleNoClub: 'Selecione um clube no contexto ativo',
    badge: 'GESTÃO',
    noClubTitle: 'Nenhum clube selecionado',
    noClubSub: 'Vincule-se a um clube para ver o elenco.',
    tabStaff: 'Staff',
    tabOfficial: 'Dados Oficiais',
    searchAthlete: 'Buscar atleta...',
    searchStaff: 'Buscar staff...',
    noStaff: 'Nenhum staff cadastrado',
    noOfficialAthletes: 'Nenhum atleta oficial encontrado',
    linkedOn: 'Vinculado em',
    linked: 'VINCULADO',
    results: 'resultados',
    last: 'Última:',
  },
  en: {
    addStaffTitle: 'Add Staff',
    labelFullName: 'Full name *',
    labelEmail: 'Email',
    labelRole: 'Role *',
    saving: 'Saving...',
    btnAddStaff: 'Add Staff',
    pageTitle: 'Staff',
    pageSubtitle: 'Club technical staff · athletes in categories',
    pageSubtitleNoClub: 'Select a club in the active context',
    badge: 'MANAGEMENT',
    noClubTitle: 'No club selected',
    noClubSub: 'Join a club to see the roster.',
    tabStaff: 'Staff',
    tabOfficial: 'Official Data',
    searchAthlete: 'Search athlete...',
    searchStaff: 'Search staff...',
    noStaff: 'No staff registered',
    noOfficialAthletes: 'No official athletes found',
    linkedOn: 'Linked on',
    linked: 'LINKED',
    results: 'results',
    last: 'Last:',
  },
};

// ─── Reusable ─────────────────────────────────────────────────────────────────

const SkeletonRows = ({ count = 4 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="h-20 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
    ))}
  </div>
);

const RoleBadge = ({ role }) => {
  const { lang } = useSportsLang();
  const colors = {
    TREINADOR: { bg: '#dbeafe', text: '#1e40af' },
    PREPARADOR_FISICO: { bg: '#fef3c7', text: '#92400e' },
    NUTRICIONISTA: { bg: '#d1fae5', text: '#065f46' },
    FISIOTERAPEUTA: { bg: '#fce7f3', text: '#9d174d' },
    MEDICO: { bg: '#ede9fe', text: '#5b21b6' },
    ANALISTA: { bg: '#f3f4f6', text: '#374151' },
    OUTRO: { bg: '#f3f4f6', text: '#374151' },
  };
  const s = colors[role] || colors.OUTRO;
  const label = (ROLE_LABELS[lang] || ROLE_LABELS.pt)[role] || role?.replace('_', ' ') || 'Staff';
  return (
    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: s.bg, color: s.text }}>
      {label}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Roster Page — Staff (uses active club context)
// Cadastro de atleta migrou para CategoryDetailPage (Fase 1 — só staff aqui).
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Add Staff Modal ──────────────────────────────────────────────────────────

const AddStaffModal = ({ open, onClose, clubId, onSuccess }) => {
  // P1.1 hotfix: backend lê staff_role (contrato canônico) — 'role' era descartado
  // e o INSERT falhava (club_staff.staff_role NOT NULL + CHECK).
  const [form, setForm] = useState({ full_name: '', email: '', staff_role: 'TREINADOR' });
  const [submitting, setSubmitting] = useState(false);
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;

  const roles = ['TREINADOR', 'PREPARADOR_FISICO', 'NUTRICIONISTA', 'FISIOTERAPEUTA', 'MEDICO', 'ANALISTA'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !clubId) return;
    setSubmitting(true);
    try {
      await AZURE.post('/roster/staff', { ...form, institution_id: clubId });
      onSuccess?.();
      onClose();
      setForm({ full_name: '', email: '', staff_role: 'TREINADOR' });
    } catch (err) {
      console.error('Error creating staff:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-extrabold" style={{ color: VUKZ.text }}>{t.addStaffTitle}</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{t.labelFullName}</label>
            <input
              type="text" required value={form.full_name}
              onChange={(e) => setForm(f => ({ ...f, full_name: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }}
            />
          </div>
          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{t.labelEmail}</label>
            <input
              type="email" value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }}
            />
          </div>
          <div>
            <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{t.labelRole}</label>
            <select
              value={form.staff_role}
              onChange={(e) => setForm(f => ({ ...f, staff_role: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }}
            >
              {roles.map(r => <option key={r} value={r}>{(ROLE_LABELS[lang] || ROLE_LABELS.pt)[r] || r.replace('_', ' ')}</option>)}
            </select>
          </div>
          <button
            type="submit" disabled={submitting}
            className="w-full py-3 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ background: VUKZ_GRADIENT }}
          >
            {submitting ? t.saving : t.btnAddStaff}
          </button>
        </form>
      </div>
    </div>
  );
};

const RosterPage = () => {
  const { clubId } = useActiveContext();
  const [staff, setStaff] = useState([]);
  const [officialAthletes, setOfficialAthletes] = useState([]);
  const [officialLink, setOfficialLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('staff');
  const [showAddStaff, setShowAddStaff] = useState(false);
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;

  const loadRoster = async () => {
    setLoading(true);
    setError(null);
    try {
      // Staff: o backend deriva a instituição de g.user_id (list_staff →
      // _get_institution_id). O clubId do frontend NÃO é autoridade — e a
      // página não pode ficar vazia esperando o ActiveContext hidratar
      // (bug do gate final: `if (!clubId) return` nunca chamava a API).
      const staffRes = await AZURE.get('/roster/staff');
      setStaff(staffRes.data?.data || []);

      // Dados oficiais: feature à parte, só quando há clube no contexto.
      if (clubId) {
        try {
          const linkData = await getOfficialLinkStatus(clubId);
          setOfficialLink(linkData);
          if (linkData?.linked) {
            const offData = await getOfficialAthletes(clubId);
            setOfficialAthletes(offData?.athletes || []);
          }
        } catch (_) {
          // No link or not available
        }
      }
    } catch (err) {
      console.error('Error loading roster:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRoster(); }, [clubId]);

  const filteredStaff = staff.filter(s => s.full_name?.toLowerCase().includes(search.toLowerCase()));
  const filteredOfficial = officialAthletes.filter(a => a.athlete_name?.toLowerCase().includes(search.toLowerCase()));

  const activeCount = staff.filter(s => s.is_active).length;
  const roleSummary = staff.reduce((acc, s) => {
    const k = s.staff_role || 'OUTRO';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">

        <PageHeader
          title={t.pageTitle}
          subtitle={`${t.pageSubtitle}${!loading && !error ? ` · ${activeCount} profissionais ativos` : ''}`}
          badgeText={t.badge}
          className="mb-6"
        />

        {/* Action buttons */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setShowAddStaff(true)}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold text-white hover:opacity-90 transition-opacity"
            style={{ background: VUKZ_GRADIENT }}
          >
            <Plus size={14} /> {t.btnAddStaff}
          </button>
        </div>

        <AddStaffModal
          open={showAddStaff}
          onClose={() => setShowAddStaff(false)}
          clubId={clubId}
          onSuccess={loadRoster}
        />

        {/* Resumo por função (VUKZ) */}
        {!loading && !error && staff.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {Object.entries(roleSummary).map(([role, count]) => (
              <div key={role} className="bg-white rounded-xl border p-3 text-center" style={{ borderColor: VUKZ.border }}>
                <div className="text-xl font-extrabold" style={{ color: VUKZ.primary }}>{count}</div>
                <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: VUKZ.textLight }}>
                  {(ROLE_LABELS[lang] || ROLE_LABELS.pt)[role] || role}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { key: 'staff', label: t.tabStaff, icon: UserCheck, count: staff.length },
            ...(officialLink?.linked ? [{ key: 'official', label: t.tabOfficial, icon: Trophy, count: officialAthletes.length }] : []),
          ].map(tabItem => (
            <button
              key={tabItem.key}
              onClick={() => setTab(tabItem.key)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all"
              style={{
                backgroundColor: tab === tabItem.key ? VUKZ.primary : 'white',
                color: tab === tabItem.key ? 'white' : VUKZ.text,
                border: `1px solid ${tab === tabItem.key ? VUKZ.primary : VUKZ.border}`,
              }}
            >
              <tabItem.icon size={16} />
              {tabItem.label}
              <span className="text-xs opacity-70">({tabItem.count})</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl border mb-6 flex items-center gap-2 px-4" style={{ borderColor: VUKZ.border }}>
          <Search size={14} style={{ color: VUKZ.textLight }} />
          <input
            type="text"
            placeholder={tab === 'official' ? t.searchAthlete : t.searchStaff}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full py-3 text-sm bg-transparent outline-none"
            style={{ color: VUKZ.text }}
          />
        </div>

        {loading ? (
          <SkeletonRows count={5} />
        ) : error ? (
          <div className="text-center py-16">
            <AlertTriangle size={32} className="mx-auto mb-3" style={{ color: '#b45309' }} />
            <p className="text-sm font-bold mb-4" style={{ color: VUKZ.text }}>Não foi possível carregar o staff.</p>
            <button onClick={loadRoster}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white hover:opacity-90"
              style={{ background: VUKZ_GRADIENT }}>
              <RefreshCw size={12} /> Tentar novamente
            </button>
          </div>
        ) : tab === 'official' ? (
          <div className="space-y-2">
            {/* Official link banner */}
            {officialLink?.link && (
              <div className="bg-white rounded-xl border p-4 mb-2 flex items-center gap-3" style={{ borderColor: VUKZ.border }}>
                <Trophy size={20} style={{ color: '#d97706' }} />
                <div className="flex-1">
                  <h4 className="text-sm font-bold" style={{ color: VUKZ.text }}>
                    {officialLink.link.official_club_name}
                  </h4>
                  <p className="text-[10px]" style={{ color: VUKZ.textLight }}>
                    {officialLink.link.official_club_city}, {officialLink.link.official_club_state} · Vinculado em {new Date(officialLink.link.approved_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#d1fae5', color: '#065f46' }}>
                  VINCULADO
                </span>
              </div>
            )}
            {filteredOfficial.length === 0 ? (
              <div className="text-center py-12 text-sm" style={{ color: VUKZ.textLight }}>
                {t.noOfficialAthletes}
              </div>
            ) : filteredOfficial.map(a => (
              <Link to={`/natacao/nadador/${a.swimmer_id}`} key={a.swimmer_id}
                className="bg-white rounded-xl border p-4 flex items-center gap-4 hover:shadow-md transition-all"
                style={{ borderColor: VUKZ.border }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                  {a.athlete_name?.substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold" style={{ color: VUKZ.text }}>{a.athlete_name}</h3>
                  <p className="text-[10px]" style={{ color: VUKZ.textLight }}>
                    {a.gender === 'male' ? '♂' : a.gender === 'female' ? '♀' : ''} · {a.total_results} {t.results}
                    {a.last_competition_date ? ` · ${t.last} ${new Date(a.last_competition_date).toLocaleDateString(lang === 'en' ? 'en-US' : 'pt-BR')}` : ''}
                  </p>
                </div>
                <ExternalLink size={14} style={{ color: VUKZ.textLight }} />
              </Link>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredStaff.length === 0 ? (
              <div className="text-center py-12 text-sm" style={{ color: VUKZ.textLight }}>
                {t.noStaff}
              </div>
            ) : filteredStaff.map(s => (
              <div key={s.staff_id} className="bg-white rounded-xl border p-4 flex items-center gap-4" style={{ borderColor: VUKZ.border }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}>
                  {s.full_name?.substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold" style={{ color: VUKZ.text }}>{s.full_name}</h3>
                  <p className="text-[10px]" style={{ color: VUKZ.textLight }}>
                    {s.email || '—'}
                    {s.categories ? ` · ${s.categories}` : ''}
                  </p>
                </div>
                {/* Escopo real de atletas (view 343: vw_staff_accessible_athletes) */}
                {Number.isFinite(s.accessible_athlete_count) && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}>
                    {s.accessible_athlete_count} atletas
                  </span>
                )}
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: s.is_active ? '#d1fae5' : '#fee2e2', color: s.is_active ? '#065f46' : '#991b1b' }}>
                  {s.is_active ? 'ATIVO' : 'INATIVO'}
                </span>
                <RoleBadge role={s.staff_role} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RosterPage;
